import type { AnalyticsDestination, AnalyticsTraits, EventProperties } from "@/lib/analytics/analytics.types";
import { isSentinelValue } from "@/lib/env";

declare global {
  interface Window {
    rdt?: (...args: unknown[]) => void;
  }
}

const PIXEL_ID = process.env.NEXT_PUBLIC_REDDIT_PIXEL_ID;

const enabled = (): boolean =>
  typeof window !== "undefined" && !!PIXEL_ID && !isSentinelValue(PIXEL_ID) && process.env.NODE_ENV !== "test";

/**
 * Do not assign window.rdt. The official snippet is `if (!w.rdt) { … pixel.js }`;
 * a pre-seeded stub would skip the vendor loader the same way gtag is *not*
 * analogous here. Queue locally until the HEAD Script installs the real rdt
 * (afterInteractive can lose the race with AnalyticsTracker useEffect).
 */
const pending: unknown[][] = [];
let pollTimer: ReturnType<typeof setInterval> | null = null;
const POLL_MS = 50;
const POLL_BUDGET_MS = 8000;

const flushRedditQueue = (): void => {
  if (typeof window.rdt !== "function") {
    return;
  }
  if (pollTimer !== null) {
    clearInterval(pollTimer);
    pollTimer = null;
  }
  const queued = pending.splice(0);
  for (const args of queued) {
    window.rdt(...args);
  }
};

const startRedditQueuePoll = (): void => {
  if (pollTimer !== null) {
    return;
  }
  const started = Date.now();
  pollTimer = setInterval(() => {
    flushRedditQueue();
    if (typeof window.rdt === "function") {
      return;
    }
    if (Date.now() - started >= POLL_BUDGET_MS) {
      if (pollTimer !== null) {
        clearInterval(pollTimer);
        pollTimer = null;
      }
      pending.length = 0;
    }
  }, POLL_MS);
};

/** Drain after the Reddit HEAD Script runs (inline snippet + Script onLoad). */
export const flushRedditPixelQueue = (): void => {
  flushRedditQueue();
};

if (typeof window !== "undefined") {
  window.addEventListener("reddit-pixel-ready", flushRedditPixelQueue);
}

const rdt = (...args: unknown[]): void => {
  if (!enabled()) return;
  if (typeof window.rdt === "function") {
    window.rdt(...args);
    return;
  }
  pending.push(args);
  startRedditQueuePoll();
};

export const redditDestination: AnalyticsDestination = {
  name: "reddit",
  isEnabled: enabled,
  pageView: () => {
    rdt("track", "PageVisit");
  },
  track: (name, _properties?: EventProperties) => {
    if (name === "sign_up") {
      rdt("track", "SignUp");
    }
  },
  identify: (traits: AnalyticsTraits) => {
    if (!PIXEL_ID) return;
    const matchKeys: { email?: string; externalId?: string } = {};
    if (typeof traits.email === "string" && traits.email.trim() !== "") {
      matchKeys.email = traits.email.trim().toLowerCase();
    }
    if (typeof traits.id === "string" && traits.id.trim() !== "") {
      matchKeys.externalId = traits.id;
    }
    if (Object.keys(matchKeys).length > 0) {
      rdt("init", PIXEL_ID, matchKeys);
    }
  },
  reset: () => {
    if (!PIXEL_ID) return;
    // Re-init without match keys so a later session cannot reuse the previous email.
    rdt("init", PIXEL_ID);
  },
};
